import { motion } from 'framer-motion'
import ResponsiveImage from '../common/ResponsiveImage'

const INFINITY_PATH =
  'M 250,125 C 350,25 475,25 475,125 C 475,225 350,225 250,125 C 150,25 25,25 25,125 C 25,225 150,225 250,125'

const AnimatedInfinity = () => (
  <div className="relative z-10 w-full h-full flex items-center justify-center px-6 py-10 md:px-10 md:py-14">
    <svg viewBox="-40 -50 580 350" className="w-full h-auto max-w-[540px]">
      <defs>
        {/* Orange (left loop) → Green (right loop) gradient */}
        <linearGradient
          id="infinityGradient"
          gradientUnits="userSpaceOnUse"
          x1="25"
          y1="125"
          x2="475"
          y2="125"
        >
          <stop offset="0%" stopColor="#c54819" />
          <stop offset="42%" stopColor="#c54819" />
          <stop offset="58%" stopColor="#1D7000" />
          <stop offset="100%" stopColor="#1D7000" />
        </linearGradient>

        {/* Faint version for the outline */}
        <linearGradient
          id="infinityGradientFaint"
          gradientUnits="userSpaceOnUse"
          x1="25"
          y1="125"
          x2="475"
          y2="125"
        >
          <stop offset="0%" stopColor="rgba(197,72,25,0.2)" />
          <stop offset="42%" stopColor="rgba(197,72,25,0.2)" />
          <stop offset="58%" stopColor="rgba(29,112,0,0.2)" />
          <stop offset="100%" stopColor="rgba(29,112,0,0.2)" />
        </linearGradient>

        <filter id="infinityGlow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="textShadow" x="-10%" y="-10%" width="120%" height="120%">
          <feDropShadow dx="0" dy="1" stdDeviation="3" floodColor="rgba(0,0,0,0.6)" />
        </filter>
      </defs>

      {/* Faint full outline — draws in on scroll */}
      <motion.path
        d={INFINITY_PATH}
        fill="none"
        stroke="url(#infinityGradientFaint)"
        strokeWidth={2.5}
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 2.5, ease: 'easeInOut' }}
      />

      {/* Single thick flowing segment — orange→green→orange */}
      <path
        d={INFINITY_PATH}
        fill="none"
        stroke="url(#infinityGradient)"
        strokeWidth={5}
        strokeLinecap="round"
        pathLength="1"
        strokeDasharray="0.25 0.75"
        filter="url(#infinityGlow)"
        className="infinity-flow"
      />

      {/* Large serif labels at loop corners */}
      <text x="120" y="-18" fill="white" fontSize="28" fontFamily="ZT Formom, Georgia, serif" textAnchor="middle" filter="url(#textShadow)">
        Educate
      </text>
      <text x="380" y="-18" fill="white" fontSize="28" fontFamily="ZT Formom, Georgia, serif" textAnchor="middle" filter="url(#textShadow)">
        Treat
      </text>
      <text x="120" y="285" fill="white" fontSize="28" fontFamily="ZT Formom, Georgia, serif" textAnchor="middle" filter="url(#textShadow)">
        Restore
      </text>
      <text x="380" y="285" fill="white" fontSize="28" fontFamily="ZT Formom, Georgia, serif" textAnchor="middle" filter="url(#textShadow)">
        Move
      </text>
    </svg>
  </div>
)

const Mission = () => {
  return (
    <section className="bg-cream-200 py-12 md:py-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 rounded-[20px] overflow-hidden border border-dark-300/40 min-h-[520px] md:min-h-[580px] lg:min-h-[620px]">
          {/* Left — nature image with animated infinity */}
          <div className="relative overflow-hidden">
            <ResponsiveImage
              src="/mission-forest.jpg"
              alt="Autumn forest with warm orange and green foliage"
              sizes="(min-width: 768px) 560px, 100vw"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-dark-900/55" />
            <AnimatedInfinity />
          </div>

          {/* Right — mission text */}
          <div className="bg-cream-100 flex flex-col justify-center px-8 md:px-12 lg:px-16 py-14 md:py-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <span className="font-sans text-brand-orange-500 text-xs tracking-[0.25em] uppercase block mb-3">
                The Model :
              </span>
              <h2 className="font-display text-dark-700 text-xl md:text-2xl font-medium mb-5 md:mb-6">
                Care That Evolves With You
              </h2>
            </motion.div>

            <motion.p
              className="font-sans text-dark-600 text-base md:text-lg leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.8, delay: 0.15, ease: 'easeOut' }}
            >
              There is no one-size-fits-all approach. As your body changes,
              your treatment should change with it:{' '}
              <span className="font-semibold">each session builds on the last</span>{' '}
              to support how you move, recover, and feel in your day-to-day life.
            </motion.p>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Mission
